import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import Header from '../components/Header';
import { useSEO, buildBreadcrumbSchema, buildArticleSchema } from '../utils/seo';
import { Calendar, MapPin, ArrowRight, Newspaper, Mail, Phone, Copy as CopyIcon, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Public press release hub at /press, plus detail pages at /press/{slug}.
 * Same component handles both (single dynamic route).
 */
const PressReleasePage = () => {
  const { slug } = useParams();
  const [list, setList] = useState([]);
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (slug) {
      axios.get(`${API_URL}/api/press/posts/${slug}`)
        .then((r) => setPost(r.data))
        .catch(() => setPost(null))
        .finally(() => setLoading(false));
    } else {
      axios.get(`${API_URL}/api/press/posts`)
        .then((r) => setList(r.data.items || []))
        .catch(() => setList([]))
        .finally(() => setLoading(false));
    }
  }, [slug]);

  const seoTitle = post
    ? post.title
    : 'Newsroom · Press Releases — Shopperden';
  const seoDesc = post
    ? post.excerpt
    : 'Shopperden newsroom — official press releases for journalists, syndication partners and the property trade press. UK, India, Canada.';
  const canonical = typeof window !== 'undefined' ? window.location.href : '';

  const breadcrumbs = post
    ? [
        { name: 'Home', url: '/' },
        { name: 'Newsroom', url: '/press' },
        { name: post.title, url: `/press/${post.slug}` },
      ]
    : [{ name: 'Home', url: '/' }, { name: 'Newsroom', url: '/press' }];

  const schemas = post
    ? [
        buildBreadcrumbSchema(breadcrumbs),
        buildArticleSchema({
          headline: post.title,
          description: post.excerpt,
          author: 'Shopperden Ltd',
          datePublished: post.release_date,
          dateModified: post.updated_at,
          url: `https://shopperden.com/press/${post.slug}`,
          image: `https://shopperden.com/icon-512.svg`,
        }),
      ]
    : [
        buildBreadcrumbSchema(breadcrumbs),
        {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Shopperden Newsroom',
          url: 'https://shopperden.com/press',
        },
      ];

  useSEO({ title: seoTitle, description: seoDesc, canonical, schema: schemas, type: post ? 'article' : 'website' });

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      toast.success('Press release copied to clipboard');
      setTimeout(() => setCopied(false), 2500);
    });
  };

  // ---------- Index view ----------
  if (!slug) {
    return (
      <div className="min-h-screen bg-[#FAF8F4]" data-testid="press-hub-page">
        <Header />
        <section className="relative bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F766E] text-white overflow-hidden">
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.07]"
            style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)', backgroundSize: '28px 28px' }}
          />
          <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 sm:py-20">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6 backdrop-blur-sm">
              <Newspaper className="h-3 w-3 text-[#FED7AA]" />
              <p className="text-[11px] tracking-[0.28em] uppercase text-[#FED7AA] font-bold">Newsroom</p>
            </div>
            <h1 className="font-['Playfair_Display'] font-semibold leading-[1.05] tracking-tight text-[2.25rem] sm:text-5xl lg:text-[4rem]">
              <span className="block">Press releases &amp;</span>
              <span className="block text-[#FED7AA] italic">syndication-ready content</span>
            </h1>
            <p className="mt-6 text-white/75 text-[15px] sm:text-lg max-w-2xl leading-relaxed">
              Official Shopperden press releases for journalists, regional press, and content-syndication partners. All releases are free to reproduce — attribution is appreciated, not required.
            </p>
            <div className="mt-7 flex flex-wrap gap-3 text-sm text-white/70">
              <a href="mailto:shopperden@yahoo.com" className="inline-flex items-center gap-2 hover:text-white" data-testid="press-email">
                <Mail className="h-4 w-4" /> shopperden@yahoo.com
              </a>
              <a href="https://wa.me/447950523316" className="inline-flex items-center gap-2 hover:text-white" data-testid="press-whatsapp">
                <Phone className="h-4 w-4" /> +44 7950 523316
              </a>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-12 lg:py-16">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[...Array(4)].map((_, i) => <div key={i} className="bg-white border border-[#E2E8F0] h-44 animate-pulse" />)}
            </div>
          ) : list.length === 0 ? (
            <div className="bg-white border border-dashed border-[#E2E8F0] p-12 text-center">
              <p className="text-[#475569]">Releases coming shortly. In the meantime, contact <a href="mailto:shopperden@yahoo.com" className="text-[#E07A3A] hover:underline">shopperden@yahoo.com</a> for tailored briefs.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {list.map((p) => (
                <Link
                  key={p.slug}
                  to={`/press/${p.slug}`}
                  className="group bg-white border border-[#E2E8F0] p-7 hover:border-[#E07A3A] hover:shadow-md transition-all"
                  data-testid={`press-card-${p.slug}`}
                >
                  <div className="flex flex-wrap items-center gap-2 text-[10px] tracking-widest uppercase font-bold mb-3">
                    <span className="text-[#C4652E]">Press Release</span>
                    {p.country && <span className="text-[#94A3B8]">· {p.country}</span>}
                    {p.release_date && (
                      <span className="text-[#94A3B8] inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> {new Date(p.release_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl text-[#0F172A] mb-2 group-hover:text-[#E07A3A] transition-colors">
                    {p.title}
                  </h3>
                  <p className="text-sm text-[#475569] leading-relaxed line-clamp-3">{p.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-[#E07A3A] text-xs tracking-widest uppercase font-semibold">
                    Read release <ArrowRight className="h-3 w-3" />
                  </span>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    );
  }

  // ---------- Detail view ----------
  return (
    <div className="min-h-screen bg-[#FAF8F4]" data-testid="press-detail-page">
      <Header />
      <article className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 py-10 sm:py-14">
        <nav className="text-xs text-[#64748B] mb-6 tracking-wider" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-[#0F172A]">Home</Link>
          <span className="mx-2">›</span>
          <Link to="/press" className="hover:text-[#0F172A]">Newsroom</Link>
          <span className="mx-2">›</span>
          <span className="text-[#0F172A]">{post?.title?.slice(0, 60)}…</span>
        </nav>

        {loading || !post ? (
          <div className="bg-white border border-[#E2E8F0] p-10 animate-pulse h-96" />
        ) : (
          <>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFF7ED] border border-[#E07A3A]/30 mb-5">
              <Newspaper className="h-3.5 w-3.5 text-[#E07A3A]" />
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#C4652E] font-bold">Press Release</p>
            </div>
            <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#0F172A] mb-4 leading-tight">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-xs text-[#64748B] mb-7">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                {new Date(post.release_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
              {post.city_slug && (
                <Link to={`/properties-in/${post.city_slug}`} className="inline-flex items-center gap-1.5 hover:text-[#E07A3A]">
                  <MapPin className="h-3.5 w-3.5" /> {post.city_slug.charAt(0).toUpperCase() + post.city_slug.slice(1)}
                </Link>
              )}
              <Button
                size="sm"
                variant="outline"
                className="ml-auto h-8 text-xs tracking-wider"
                onClick={() => copyToClipboard(post.content?.replace(/<[^>]+>/g, '\n').replace(/\n+/g, '\n').trim())}
                data-testid="press-copy-btn"
              >
                {copied ? <><Check className="h-3 w-3 mr-1.5" /> Copied</> : <><CopyIcon className="h-3 w-3 mr-1.5" /> Copy plain text</>}
              </Button>
            </div>

            <div
              className="bg-white border border-[#E2E8F0] p-7 sm:p-10 prose prose-slate max-w-none press-body"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content || '', {
                ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a', 'blockquote', 'hr', 'span', 'div'],
                ALLOWED_ATTR: ['href', 'title', 'target', 'rel'],
                ALLOW_DATA_ATTR: false,
              }) }}
            />

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Link to={`/properties-in/${post.city_slug || ''}`} className="bg-[#E07A3A] hover:bg-[#C4652E] text-white px-6 py-4 text-sm tracking-widest uppercase font-bold text-center transition-colors">
                Browse {post.city_slug ? post.city_slug.charAt(0).toUpperCase() + post.city_slug.slice(1) : 'live'} listings →
              </Link>
              <Link to="/press" className="bg-white border border-[#E2E8F0] hover:border-[#E07A3A] text-[#0F172A] px-6 py-4 text-sm tracking-widest uppercase font-bold text-center transition-colors">
                More from the newsroom
              </Link>
            </div>
          </>
        )}
      </article>
    </div>
  );
};

export default PressReleasePage;
